import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import type { Tag } from '~/services/TagsService';

import { TAG_TYPE_OPTIONS, TagTypeValue } from './constants';
import { useEndTag, useGetTags, useGetTagsMetadata } from './hooks';
import { DATAGRID_HIDE_SEPARATORS_SX } from './styles';

interface Props {
  onCreate: () => void;
  onEdit: (tagId: string) => void;
  onViewContent: (tagName: string) => void;
}

export default function TagsList({ onCreate, onEdit, onViewContent }: Props) {
  const [endTagId, setEndTagId] = useState<string | null>(null);
  const [tagTypeFilter, setTagTypeFilter] = useState<TagTypeValue | ''>('');
  const endTag = useEndTag();

  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 100,
    page: 0,
  });

  const {
    data = null,
    isLoading = false,
    error = null,
  } = useGetTags({
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    sortBy: 'updated_at',
    sortOrder: 'desc',
    tagType: tagTypeFilter || undefined,
  });
  const tagIds = data?.result?.map((tag) => tag.id) ?? [];
  const {
    assignmentCounts,
    scheduleSummaries,
    inactiveTagIds,
    isLoading: isTagsMetadataLoading,
  } = useGetTagsMetadata(tagIds);

  const sortedRows = useMemo(() => {
    const rows = data?.result ?? [];
    if (isTagsMetadataLoading || inactiveTagIds.size === 0) {
      return rows;
    }

    const active = rows.filter((tag) => !inactiveTagIds.has(tag.id));
    const inactive = rows.filter((tag) => inactiveTagIds.has(tag.id));
    return [...active, ...inactive];
  }, [data?.result, inactiveTagIds, isTagsMetadataLoading]);

  const createCountColumn = (field: string, headerName: string): GridColDef<Tag> => ({
    field,
    headerName,
    minWidth: 160,
    sortable: false,
    renderCell: (params: GridRenderCellParams<Tag>) => {
      const counts = assignmentCounts.get(params.row.id);
      if (isTagsMetadataLoading && !counts) {
        return <Box sx={{ width: '100%', py: 1 }}>...</Box>;
      }
      return <Box sx={{ width: '100%', py: 1 }}>{counts?.[field as keyof typeof counts] ?? 0}</Box>;
    },
    display: 'flex',
  });

  const handleTagTypeFilterChange = (event: SelectChangeEvent<TagTypeValue | ''>) => {
    setTagTypeFilter(event.target.value as TagTypeValue | '');
    setPaginationModel((currentModel) => ({ ...currentModel, page: 0 }));
  };

  const columns: Array<GridColDef<Tag>> = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams<Tag>) => (
        <Box sx={{ width: '100%', py: 1 }}>
          <Tooltip title={params.row.id}>
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.row.name}
            </Box>
          </Tooltip>
        </Box>
      ),
      display: 'flex',
    },
    {
      field: 'tagType',
      headerName: 'Type',
      minWidth: 170,
      renderCell: (params: GridRenderCellParams<Tag>) => {
        const tagType = TAG_TYPE_OPTIONS.find((option) => option.value === params.row.tagType);

        return <Box sx={{ width: '100%', py: 1 }}>{tagType?.label ?? params.row.tagType}</Box>;
      },
      display: 'flex',
    },
    createCountColumn('destinations', 'Destinations'),
    createCountColumn('hotels', 'Hotels'),
    createCountColumn('experiences', 'Experiences'),
    {
      field: 'schedules',
      headerName: 'Schedules',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Tag>) => {
        const schedules = scheduleSummaries.get(params.row.id);

        if (isTagsMetadataLoading && !schedules) {
          return <Box sx={{ width: '100%', py: 1 }}>Loading schedules...</Box>;
        }

        if (!schedules || schedules.length === 0) {
          return <Box sx={{ width: '100%', py: 1 }}>No schedules</Box>;
        }

        return (
          <Box sx={{ width: '100%', py: 1 }}>
            {schedules.map((schedule) => (
              <Box key={schedule.id}>{schedule.text}</Box>
            ))}
          </Box>
        );
      },
      display: 'flex',
    },
    {
      field: 'actions',
      headerName: '',
      width: 240,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      cellClassName: 'tags-edit-cell',
      renderCell: (params: GridRenderCellParams<Tag>) => (
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} width="100%" height="100%">
          <Button
            variant="text"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(params.row.id);
            }}
          >
            Edit
          </Button>
          <Button
            variant="text"
            color="success"
            onClick={(event) => {
              event.stopPropagation();
              onViewContent(params.row.name);
            }}
          >
            Content
          </Button>
          <Button
            variant="text"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              setEndTagId(params.row.id);
            }}
            disabled={endTag.isPending}
          >
            End
          </Button>
        </Box>
      ),
      display: 'flex',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ pt: 3 }}>
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
          Create Tag
        </Button>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="tag-type-filter-label">Tag type</InputLabel>
          <Select
            labelId="tag-type-filter-label"
            label="Tag type"
            value={tagTypeFilter}
            onChange={handleTagTypeFilterChange}
          >
            <MenuItem value="">All tag types</MenuItem>
            {TAG_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <DataGrid
        loading={isLoading}
        rows={sortedRows}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: Tag) => row.id}
        onRowClick={(params) => onEdit(params.row.id)}
        paginationMode="server"
        rowCount={data?.pagination?.total_items ?? 0}
        slots={{ pagination: GridPagination }}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => (inactiveTagIds.has(params.row.id) ? 'tags-row--inactive' : '')}
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            px: 4,
          },
          '& .MuiDataGrid-columnHeader': {
            px: 4,
          },
          '& .MuiDataGrid-cell.tags-edit-cell': {
            alignItems: 'center',
          },
          '& .tags-row--inactive': {
            opacity: 0.5,
          },
          ...DATAGRID_HIDE_SEPARATORS_SX,
        }}
      />
      <Dialog open={!!endTagId} onClose={endTag.isPending ? undefined : () => setEndTagId(null)}>
        <DialogTitle>End Tag</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to end this tag? All active schedules will have their end date set to now.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setEndTagId(null)} disabled={endTag.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (endTagId) {
                await endTag.mutateAsync(endTagId);
                setEndTagId(null);
              }
            }}
            disabled={endTag.isPending}
          >
            End Tag
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
